import { t } from '@citizen-walk/shared-i18n';
import {
  Button,
  PageErrorBoundary,
  PanoramaViewer,
  detectPanorama,
  useToast,
} from '@citizen-walk/shared-ui';
import {
  evaluateColorRule,
  findOtherOption,
  isOtherTextMissing,
  formatErrorForToast,
  fromUnknownError,
  resolveFieldMaxLength,
  type GeoPoint,
  type PostPhotoMeta,
  type PostVisibility,
} from '@citizen-walk/shared-types';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { recoverFromFirestoreAssertion } from '../../lib/firebase';
import { isRecoverableFirestoreError } from '../../lib/firestoreRecovery';
import { useDistrict } from '../district';
import { PostLocationPicker } from '../map';
import { haversineDistanceM } from './geoDistance';
import { useDistrictColorRule } from './ColorBadge';
import { useDeviceOrientation } from './useDeviceOrientation';
import { useGeolocation } from './useGeolocation';
import { createPost } from './usePosts';
import { COMMENT_MAX, COUNTER_CLASS, INPUT_CLASS, NICKNAME_MAX, TITLE_MAX } from './constants';
// Sprint BG-DataSettings 27 (旧アプリ揃え): visibility (公開範囲) + nickname inline 編集を
// 撤廃し、postFields の label / maxLength / mode (hidden) をデータ駆動で参照する。
// TITLE_MAX / COMMENT_MAX / NICKNAME_MAX は後方互換のため import を保持しているだけで、
// 実際の上限は postFields[].optionsMeta.maxLength → resolveFieldMaxLength() で解決。
import { checkPhotoSize, preprocessPhoto } from './photo';
import { PostExtraFieldsForm } from './PostExtraFieldsForm';
import {
  collectOtherTextValues,
  findMissingRequiredPostFields,
  getVisiblePostFields,
} from './postFieldsHelpers';
import type { FieldDef } from '@citizen-walk/shared-types';

export interface PostFormProps {
  authorUid: string;
  /**
   * 投稿先 districtId (必須)。AppHome / PostPanel が readiness gate 通過後の `district.id` を渡す。
   * 地区フォールバック根絶 (2026-06-08): 旧 phase2-default 既定は撤去 (省略不可)。
   */
  districtId: string;
  /** 既定ニックネーム (uid から派生した anon-xxxxxx 等)。詳細設定で上書き可。 */
  defaultNickname: string;
  /** 投稿成功時のコールバック (任意)。 */
  onPosted?: (postId: string) => void;
  /**
   * REQ-4.1.11: 撮影前注意モーダル (PhotoNoticeModal) を了承済か。
   * 未了承で写真選択 button を押すと `onRequestPhotoNotice` を呼ぶ (旧 PostTab L238-245 と同 flow)。
   */
  photoAcknowledged?: boolean;
  /** 未了承時に撮影注意モーダルを開くコールバック。 */
  onRequestPhotoNotice?: () => void;
  /**
   * 紐づくセッション ID (user 第 2 弾、docs/04 §4-2 Post.sessionId)。
   * null = "全セッション" 扱いで Firestore に null を書く。
   */
  sessionId?: string | null;
  /**
   * Phase E (old2 投稿フロー移植): セッション未参加でも投稿可にするか。
   * `false` (既定) で `sessionId == null` のとき、submit 時に toast 警告 + return で投稿不可
   * (old2 PostTab L319-322 の session gating を踏襲)。
   */
  allowPostingWithoutSession?: boolean;
  /**
   * Phase E (old2 投稿フロー移植): ユーザー設定 (consentAcceptedAt) 未完了か。
   * true のとき old2 PostTab L510-514 の警告 (⚠ 左上のメニューから…) を表示 + canSubmit=false。
   */
  userSettingsIncomplete?: boolean;
  /**
   * キャンセル時のコールバック (old2 PostTab L516-521、`navigate(-1)`)。
   * 未指定なら router の history を 1 つ戻る。
   */
  onCancel?: () => void;
  /**
   * 投稿可能エリア中心 (user 第 12 弾、GAP-U3)。
   * `postAreaRadiusM` と両方指定された場合のみ距離判定し、エリア外なら警告 + submit disable。
   */
  postAreaCenter?: GeoPoint | null;
  postAreaRadiusM?: number | null;
  /**
   * 投稿先 District の postFields 定義 (Sprint BG-DataSettings 5)。
   * 渡されない場合は組込み (title/comment/photo/location) 固定描画 (旧 v1 dataSettings 未設定地区との後方互換)。
   * PostPanel から `resolvePostFields(district)` の戻り値を渡す前提。
   */
  postFields?: FieldDef[];
}

type SubmitState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string };

/**
 * 投稿フォーム — old2 `apps/user/src/pages/tabs/PostTab.tsx` を逐語移植 (Phase E)。
 *
 * old2 から踏襲した点:
 *   - **描画順の完全動的化** (old2 L502-505): `district.dataSettings.postFields` を
 *     `getVisiblePostFields` (displayOrder 昇順 / mode!=='hidden') でループし、`renderField` switch で
 *     組込 (photo/title/location/comment) は専用 UI、その他は generic renderer (PostExtraFieldsForm) へ。
 *     admin の項目並べ替え (displayOrder) にそのまま追従する。
 *   - **session gating** (old2 L311-322): 現セッション未参加 (`sessionId == null`) かつ
 *     `!allowPostingWithoutSession` のとき toast 警告 + return。
 *   - **profileStatus** (old2 L510-514): `userSettingsIncomplete` で警告バナー + canSubmit=false。
 *   - **必須未入力 toast** (old2 L326-341): `「{label}」は必須です` 方式。
 *   - **キャンセルボタン** (old2 L516-521)。
 *   - **逐語文言**: title placeholder「短く分かりやすく」/ 位置情報「現在地で取得」「地図で指定」
 *     「精度: ±{m}m」/ 写真「撮影」「ギャラリー」/「投稿する」(shared-i18n post.* と一致)。
 *   - **マーカー色は ColorRule 連動** (old2 L94-98): 入力中 extraFields を評価して地図ピン色へ反映。
 *
 * v2 で調整した点:
 *   - 位置取得は `useGeolocation` state machine + `PostLocationPicker` (lazy maplibre) を使用。
 *   - 写真前処理 / outbox / Firestore 保存は既存 `createPost` 経路 (orientation snapshot も継続)。
 *   - 撮影注意モーダルは PostPanel が所有 (`photoAcknowledged` / `onRequestPhotoNotice` props)。
 *   - 配色は v2 preset (primary/slate/danger/warning)、inline style 禁止。
 */

/**
 * GPS 取得不可かつ投稿エリア未設定のときの地図初期中心 (geo-3、docs/111 Wave 1)。日本のほぼ中心。
 * これ自体は「選択された位置」ではなく地図の表示中心に過ぎず、ユーザーがタップして初めて位置が確定する
 * (showMarker=false)。エリア設定地区では postAreaCenter を優先するため、この値が使われるのは稀。
 */
const FALLBACK_MAP_CENTER: GeoPoint = { latitude: 36.2048, longitude: 138.2529 };

function AccuracyBadge({ accuracy }: { accuracy: number }) {
  const rounded = Math.round(accuracy);
  let cls: string;
  let label: string;
  if (accuracy <= 10) {
    cls = 'bg-emerald-100 text-emerald-800 border-emerald-300';
    label = '高';
  } else if (accuracy <= 50) {
    cls = 'bg-amber-100 text-amber-800 border-amber-300';
    label = '中';
  } else {
    cls = 'bg-danger-100 text-danger-700 border-danger-300';
    label = '低';
  }
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border ${cls}`}
      title={`GPS 精度 (±${rounded} m)`}
      aria-label={`GPS 精度 ${label} (誤差 約 ${rounded} メートル)`}
    >
      精度 {label} (±{rounded} m)
    </span>
  );
}

/** Card — old2 PostTab L896-922 と同じ「丸番号 + title + 必須 + ヒント」ラッパ。 */
function Card({
  step,
  title,
  required,
  hint,
  children,
}: {
  step: number;
  title: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-baseline gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold">
          {step}
        </span>
        <h3 className="font-bold text-slate-900 text-sm">{title}</h3>
        {required && <span className="text-xs text-danger-600 font-medium">必須</span>}
        {hint && <span className="text-xs text-slate-500 ml-auto">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

export function PostForm({
  authorUid,
  districtId,
  defaultNickname,
  onPosted,
  photoAcknowledged = true,
  onRequestPhotoNotice,
  sessionId = null,
  allowPostingWithoutSession = false,
  userSettingsIncomplete = false,
  onCancel,
  postAreaCenter = null,
  postAreaRadiusM = null,
  postFields,
}: PostFormProps) {
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  // docs/111 E-2: defaultNickname は親 (PostPanel→AppHome useResolvedNickname) が membership doc
  // から解決した値。membership の getDoc が PostForm mount 後に完了するケース (新規ユーザーが投稿タブを
  // 開いた直後 = AppHome 再マウント直後) で useState に固定すると、読込前の `anon-${uid前6}` fallback が
  // そのまま焼き込まれてしまう (本番 箱根 incognito で再現)。nickname はユーザーが PostForm 内で編集しない
  // (inline 編集は撤廃済) ため state 化せず prop を直接参照し、membership 解決後の正規名に追従させる。
  const nickname = defaultNickname;
  const [visibility] = useState<PostVisibility>('public');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  // docs/27: 元ファイルから判定した 360度写真メタ。前処理 (XMP 消失) の前に判定して保持する。
  const [photoMeta, setPhotoMeta] = useState<PostPhotoMeta | null>(null);
  // 判定は自動だが、誤判定 (2:1 の通常パノラマ等) は投稿者が「平面として扱う」で外せる。
  const [treatAsPanorama, setTreatAsPanorama] = useState(true);
  // old2 PostTab L72: 写真前処理中 (preprocess) は二重押し防止のため「処理中」を明示する。
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const [state, setState] = useState<SubmitState>({ status: 'idle' });
  // Sprint BG-DataSettings 5: extraFields の値辞書 (key=FieldDef.key、値=unknown)。
  // PostExtraFieldsForm が onChange で全体差し替え (UserFieldsForm と同 pattern)。
  const [extraValues, setExtraValues] = useState<Record<string, unknown>>({});
  // 現在地は継続取得 (watch=true、2026-06-12): 投稿画面表示中ずっと現在位置を追従する。
  const geo = useGeolocation({ watch: true });
  // Sprint BF Part 1 (GAP-U1): デバイス方位センサーを購読、投稿時に snapshot を保存。
  const deviceOrientation = useDeviceOrientation();
  // 投稿位置の確定状態 (2026-06-12)。
  //   - null  = 追跡中: 継続取得した現在地を投稿位置とする (地図ピン=赤+モーション)。
  //   - 設定済 = 確定: その位置に固定 (地図ピン=赤・モーション無し)。地図タップ / 「現在地で取得」確定 /
  //              エリア中心へ戻る / 長押し override で確定する。source は GPS 由来か手動指定かを保持。
  const [locked, setLocked] = useState<{
    location: GeoPoint;
    accuracy: number | null;
    source: 'gps' | 'manual';
  } | null>(null);
  const tracking = locked === null;

  // Sprint AG: old2 PostTab L172-189 と同 pattern — MapPage 長押し「ここで投稿」から navigate された
  // route state.locationOverride を読み取り、manualLocation に反映。再適用防止のため state を消費。
  const routerLocation = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const stateAny = routerLocation.state as { locationOverride?: GeoPoint } | null;
    const override = stateAny?.locationOverride;
    if (
      override &&
      typeof override.latitude === 'number' &&
      typeof override.longitude === 'number' &&
      Number.isFinite(override.latitude) &&
      Number.isFinite(override.longitude)
    ) {
      setLocked({
        location: { latitude: override.latitude, longitude: override.longitude },
        accuracy: null,
        source: 'manual',
      });
      navigate(routerLocation.pathname, { replace: true, state: null });
    }
  }, [routerLocation.state, routerLocation.pathname, navigate]);
  const { toast } = useToast();
  // Task C: 地区別 投稿写真設定 (圧縮/無圧縮/受付上限)。地区未解決 (テスト等) では undefined → 既定動作。
  const photoConfig = useDistrict()?.photoConfig;

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // mount 時にデバイス方位センサーを購読開始。現在地は useGeolocation の watch が継続取得するため
  // ここでの geo.capture() は不要 (2026-06-12、継続取得化)。
  useEffect(() => {
    void deviceOrientation.requestPermissionAndStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handlePhotoChange(file: File | null, meta: PostPhotoMeta | null = null) {
    setPhoto(file);
    setPhotoMeta(file ? meta : null);
    setTreatAsPanorama(true);
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(file ? URL.createObjectURL(file) : null);
  }
  const isPanorama = photoMeta?.projection === 'equirectangular' && treatAsPanorama;
  const effectivePhotoMeta: PostPhotoMeta | null = photoMeta
    ? isPanorama
      ? photoMeta
      : { ...photoMeta, projection: 'flat', detectedBy: null }
    : null;

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    };
  }, [photoPreviewUrl]);

  function pickPhoto(source: 'camera' | 'gallery') {
    if (!photoAcknowledged) {
      onRequestPhotoNotice?.();
      return;
    }
    if (source === 'camera') cameraInputRef.current?.click();
    else galleryInputRef.current?.click();
  }

  async function onPhotoInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    e.target.value = ''; // 同じ file の再選択で onChange を発火させるためクリア
    if (!f) {
      handlePhotoChange(null);
      return;
    }
    // old2 PostTab L252-268: 選択時に前処理 (リサイズ + EXIF 削除) してプレビュー。
    // 4-12MP 写真で 0.5〜1 秒のメインスレッドブロックがあるため「処理中」を明示し二重押しを防ぐ。
    setPhotoProcessing(true);
    try {
      // docs/27: 360度写真 (XMP GPano / 2:1) は前処理で XMP が消える前に元ファイルから判定する。
      const detection = await detectPanorama(f);
      const meta: PostPhotoMeta = {
        projection: detection.projection,
        width: detection.width,
        height: detection.height,
        poseHeadingDeg: detection.poseHeadingDeg,
        detectedBy: detection.detectedBy,
      };
      // Task C: 地区設定で圧縮/無圧縮を切替。処理後に受付上限 (maxUploadSizeMB) を検証し早期に弾く。
      const processed = await preprocessPhoto(f, photoConfig, {
        equirectangular: detection.projection === 'equirectangular',
      });
      const sz = checkPhotoSize(processed, photoConfig);
      if (!sz.ok) {
        toast(
          'error',
          `画像サイズが大きすぎます (上限 ${sz.maxMB.toFixed(0)}MB、ファイル ${sz.actualMB.toFixed(1)}MB)。`,
        );
        handlePhotoChange(null);
        return;
      }
      handlePhotoChange(processed, meta);
    } catch (err) {
      console.error(err);
      toast('error', '写真の処理に失敗しました。');
    } finally {
      setPhotoProcessing(false);
    }
  }

  const titleTrimmed = title.trim();
  const nicknameTrimmed = nickname.trim();
  // 継続取得した現在地サンプル (watch)。確定中 (locked) はこれを使わず locked を投稿位置とする。
  const liveSample = geo.state.status === 'ready' ? geo.state.sample : null;
  // 有効位置: 確定済 (locked) > 追跡中の現在地。locked の source/accuracy を尊重する。
  const effectiveLocation: GeoPoint | null = locked
    ? locked.location
    : (liveSample?.location ?? null);
  const effectiveAccuracy: number | null = locked
    ? locked.accuracy
    : (liveSample?.accuracy ?? null);
  const locationSource: 'gps' | 'manual' = locked ? locked.source : 'gps';
  const hasLocation = effectiveLocation !== null;
  // geo-3 (docs/111 Wave 1): 地図は常に表示し (GPS 取得不可でもタップで手動指定できるよう)、中心は
  // 有効位置 > 投稿エリア中心 > 日本中心 の順で解決する。marker は位置確定 (hasLocation) 後のみ出す。
  const mapDisplayCenter: GeoPoint = effectiveLocation ?? postAreaCenter ?? FALLBACK_MAP_CENTER;
  // geo-3: 位置確定済は zoom 16、未確定でエリア中心ありは 14 (エリア俯瞰)、エリアも無く日本中心 fallback の
  // ときは 5 (全国から目的地へ pan できる広域)。旧 PostTab L108 の `location ? 16 : 14` を踏襲・拡張。
  const mapZoom = hasLocation ? 16 : postAreaCenter ? 14 : 5;

  // old2 PostTab L90-98: 投稿マーカー色は ColorRule 連動。入力中の extraFields を評価して色を決め、
  // 地図ピン (PostLocationPicker) に渡す。colorRule 未設定地区は old2 既定 pinkish (#ec4899)。
  const colorRule = useDistrictColorRule();
  const markerColor = useMemo(() => {
    if (!colorRule) return '#ec4899';
    return evaluateColorRule(colorRule, { extraFields: extraValues }).color;
  }, [colorRule, extraValues]);

  const areaCheck = useMemo<
    | { status: 'skipped' }
    | { status: 'inside'; distanceM: number; radiusM: number }
    | { status: 'outside'; distanceM: number; radiusM: number }
  >(() => {
    // geo-4 (docs/111 Wave 1): location が hidden の地区はユーザーが位置を確認/調整する UI を持たないため、
    // 投稿エリア制限で「理由不明のままボタンが恒久 disable」になる silent block を避ける (gating を skip)。
    const locHidden = postFields?.find((f) => f.key === 'location')?.mode === 'hidden';
    if (locHidden) return { status: 'skipped' };
    if (!postAreaCenter) return { status: 'skipped' };
    if (
      typeof postAreaRadiusM !== 'number' ||
      !Number.isFinite(postAreaRadiusM) ||
      postAreaRadiusM <= 0
    ) {
      return { status: 'skipped' };
    }
    if (!effectiveLocation) return { status: 'skipped' };
    const distanceM = haversineDistanceM(effectiveLocation, postAreaCenter);
    return distanceM <= postAreaRadiusM
      ? { status: 'inside', distanceM, radiusM: postAreaRadiusM }
      : { status: 'outside', distanceM, radiusM: postAreaRadiusM };
  }, [postAreaCenter, postAreaRadiusM, effectiveLocation, postFields]);

  // Sprint BG-DataSettings 5: 必須 extraFields の未入力 label 一覧。postFields 未渡しなら空配列。
  const missingExtraLabels = useMemo(
    () => (postFields ? findMissingRequiredPostFields(postFields, extraValues) : []),
    [postFields, extraValues],
  );

  // old2 PostTab L55-62: 全可視 field (組込 + 地区独自) を displayOrder 昇順で取得。renderField の唯一 source。
  const visibleFields = useMemo(
    () => (postFields ? getVisiblePostFields(postFields) : []),
    [postFields],
  );

  // 組込 field の解決 (admin が label / maxLength / mode='hidden' を編集できるようにデータ駆動)。
  const findPostField = (...keys: string[]) => postFields?.find((f) => keys.includes(f.key));
  const titleField = findPostField('title');
  // 本文は post.comment に統一。新テンプレは全モード 'comment'。'detail' は旧テンプレを保存済みの
  // 既存地区との後方互換で許容 (どちらも同じ本文 textarea → post.comment に保存)。
  const bodyField = findPostField('comment', 'detail');
  const locationField = findPostField('location');

  const titleMax = titleField ? (resolveFieldMaxLength(titleField) ?? TITLE_MAX) : TITLE_MAX;
  const titleRequired = (titleField?.mode ?? 'required') === 'required';
  const titleHidden = titleField?.mode === 'hidden';
  const bodyMax = bodyField ? (resolveFieldMaxLength(bodyField) ?? COMMENT_MAX) : COMMENT_MAX;
  const locationHidden = locationField?.mode === 'hidden';

  // fvg-2 (docs/111 Wave 1): session 必須地区で現セッション未選択/失効 (sessionId=null) のとき。
  // 旧実装は canSubmit に session 条件が無く、ボタンは押せるのに handleSubmit の toast で弾かれ続け、
  // ユーザーは理由も分からず抜け出せなかった。canSubmit に含めてボタンを正しく disable する。
  // 再選択 UI (SessionPicker) は PostPanel が本フォーム上部に出す。
  const sessionRequiredButMissing = !allowPostingWithoutSession && !sessionId;

  // old2 PostTab L395-396: ユーザー設定未完了なら投稿不可。
  const canSubmit =
    state.status !== 'submitting' &&
    !photoProcessing &&
    !userSettingsIncomplete &&
    !sessionRequiredButMissing &&
    (titleHidden || (titleTrimmed.length > 0 && titleTrimmed.length <= titleMax)) &&
    nicknameTrimmed.length > 0 &&
    nicknameTrimmed.length <= NICKNAME_MAX &&
    comment.length <= bodyMax &&
    // fvg-1 (docs/111 Wave 1): location は createPost が必須とするため、mode='hidden' でも常に必要
    // (hidden は UI を隠すだけで GPS は裏で取得する)。旧実装は (locationHidden || hasLocation) で
    // hidden + GPS 失敗時にボタンが有効化され、押すと handleSubmit の `!effectiveLocation` early-return で
    // silent に無反応だった。hasLocation を必須にしてボタンを正しく disable + 理由提示する。
    hasLocation &&
    areaCheck.status !== 'outside' &&
    // G-1 (docs/111 Wave 4): 必須 extraFields の未入力をボタン段階で弾く。旧実装は canSubmit に
    // 含めず、押して初めて 1 件ずつ toast (focus 移動なし) でユーザーが詰まりやすかった。
    // 未入力 label は下部の「未入力の必須項目」表示でも提示する。
    missingExtraLabels.length === 0;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state.status === 'submitting' || photoProcessing) return;

    // old2 PostTab L311-322: 現セッション未参加 && !allowPostingWithoutSession なら投稿不可。
    // fvg-2: canSubmit でボタンは既に disable 済。これは念のための二重 guard (userSettingsIncomplete と同型)。
    if (sessionRequiredButMissing) {
      toast('warning', 'セッションに参加してから投稿してください。');
      return;
    }

    // old2 PostTab L323: title / location 未取得は静かに return (UI で disable 済)。
    if (!titleHidden && !titleTrimmed) return;
    if (!locationHidden && !effectiveLocation) return;

    // old2 PostTab L326-341: 必須チェック (dataSettings.postFields 基準)、未入力は toast『「{label}」は必須です』。
    if (postFields) {
      for (const f of visibleFields) {
        if (f.mode !== 'required') continue;
        if (f.key === 'photo') {
          if (!photo) {
            toast('warning', `「${f.label}」は必須です`);
            return;
          }
          continue;
        }
        // title / location / comment は上の組込み check / UI disable で担保。
        if (
          f.key === 'title' ||
          f.key === 'location' ||
          f.key === 'comment' ||
          f.key === 'detail'
        ) {
          continue;
        }
        const v = extraValues[f.key];
        const empty =
          v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
        if (empty) {
          toast('warning', `「${f.label}」は必須です`);
          return;
        }
        if (isOtherTextMissing(f, extraValues, v)) {
          toast(
            'warning',
            `「${f.label}」の「${findOtherOption(f)?.label ?? 'その他'}」の内容を入力してください`,
          );
          return;
        }
      }
    }

    // userSettingsIncomplete は canSubmit で disable 済だが、念のため二重 guard (old2 L510-514)。
    if (userSettingsIncomplete) return;

    if (!effectiveLocation) return;

    setState({ status: 'submitting' });
    try {
      const orientationSnap = deviceOrientation.getCurrentOrientation();
      // 描画対象 (= visible で組込以外) の field 値のみ extraFields に書込む。
      const extraFieldsPayload: Record<string, unknown> = {};
      for (const f of visibleFields) {
        if (f.key === 'photo' || f.key === 'location') continue;
        // title / comment / detail は PostForm 本体の state (title/comment) を直接保存するため skip。
        // category は generic renderer で extraValues に収集されるため **書き込む** (old2 PostTab L361
        // `extraFields = {...extraValues}` 踏襲)。colorRule.fieldKey='category' が extraFields.category を
        // 参照するので、ここで除外すると地図/一覧の色分けが効かなくなる。
        if (f.key === 'title' || f.key === 'comment' || f.key === 'detail') {
          continue;
        }
        const v = extraValues[f.key];
        if (v === undefined) continue;
        if (typeof v === 'string' && v === '') continue;
        if (Array.isArray(v) && v.length === 0) continue;
        extraFieldsPayload[f.key] = v;
      }
      // 選択肢「その他」の記入内容 ({key}__other)。未記入は書かない。
      for (const [k, text] of Object.entries(collectOtherTextValues(visibleFields, extraValues))) {
        if (text !== null) extraFieldsPayload[k] = text;
      }
      const id = await createPost({
        authorUid,
        authorNickname: nicknameTrimmed,
        title: titleTrimmed,
        comment: comment.trim() === '' ? undefined : comment.trim(),
        location: effectiveLocation,
        accuracy: effectiveAccuracy,
        source: locationSource,
        visibility,
        districtId,
        sessionId,
        photo,
        photoConfig,
        photoMeta: effectivePhotoMeta,
        orientation: orientationSnap,
        extraFields: Object.keys(extraFieldsPayload).length > 0 ? extraFieldsPayload : undefined,
      });
      setTitle('');
      setComment('');
      handlePhotoChange(null);
      setLocked(null); // 追跡中に戻す
      setExtraValues({});
      setState({ status: 'idle' });
      if (!id.startsWith('outbox:')) {
        toast('success', '投稿しました');
      }
      onPosted?.(id);
    } catch (err) {
      // ARCB-4 (docs/111 Wave 2): 投稿 setDoc の同期パスで #7341 等の回復可能アサーションが発火した場合、
      // この catch が吸収するため window の error/unhandledrejection リスナーに届かず自動復帰が走らない。
      // ここで直接検知し、キャッシュ破棄 + reload の自動復帰を発火する (1 セッション 1 回ガード付き)。
      if (isRecoverableFirestoreError(err)) {
        // 先に error 状態へ倒し、reload が起きない環境 (sessionStorage 不可で 1 回ガード済 等) でも
        // ボタンが submitting のまま固着しないようにする。
        setState({
          status: 'error',
          message: '通信の内部エラーを検知しました。再読み込みします。',
        });
        toast('warning', '通信の内部エラーを検知しました。復旧のため画面を再読み込みします。');
        recoverFromFirestoreAssertion(err);
        return;
      }
      const appErr = fromUnknownError(err);
      setState({ status: 'error', message: appErr.message });
      toast('error', `送信失敗: ${formatErrorForToast(appErr)}`);
    }
  }

  // ==== フィールド描画 (old2 PostTab L399-495 renderField 踏襲) ====================
  function renderField(f: FieldDef, step: number): ReactNode {
    const required = f.mode === 'required';
    const hint = f.description || undefined;

    switch (f.key) {
      case 'photo':
        // old2 PostTab L404-429: 写真処理中スピナー / プレビュー / 撮影・ギャラリー 2 button split。
        // aria-label に「写真 (任意)」を含めて PostForm.test.tsx の getByLabelText 互換を保つ。
        return (
          <Card
            key={f.key}
            step={step}
            title={f.label}
            required={required}
            hint={required ? undefined : '任意'}
          >
            {!photoAcknowledged ? (
              <p className="text-xs text-notice-700 my-1">
                撮影前の注意 (REQ-4.1.11) を未確認のため写真添付できません。
                {onRequestPhotoNotice && (
                  <button
                    type="button"
                    onClick={onRequestPhotoNotice}
                    className="ml-2 text-xs text-primary-600 hover:text-primary-700 underline bg-transparent border-none p-0 cursor-pointer"
                  >
                    注意を確認する
                  </button>
                )}
              </p>
            ) : photoProcessing ? (
              <div className="w-full h-40 flex flex-col items-center justify-center bg-slate-50 border border-slate-200 rounded-lg gap-2">
                <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-slate-600">写真を処理中…</p>
              </div>
            ) : photoPreviewUrl ? (
              <div>
                <div className="relative">
                  {isPanorama ? (
                    <PanoramaViewer
                      src={photoPreviewUrl}
                      baseHeadingDeg={photoMeta?.poseHeadingDeg ?? 0}
                      className="w-full h-72 rounded-lg border border-slate-200"
                      enableFullscreen={false}
                      alt="360度写真のプレビュー"
                    />
                  ) : (
                    <img
                      src={photoPreviewUrl}
                      alt="プレビュー"
                      className="w-full max-h-72 object-cover rounded-lg border border-slate-200"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => handlePhotoChange(null)}
                    className="absolute top-2 right-2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full border-none cursor-pointer"
                    style={{ zIndex: 30 }}
                  >
                    × 削除
                  </button>
                </div>
                {photoMeta?.projection === 'equirectangular' && (
                  <label className="mt-1 flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={treatAsPanorama}
                      onChange={(e) => setTreatAsPanorama(e.target.checked)}
                    />
                    360度写真として投稿する（ドラッグで見回せます）
                  </label>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2" aria-label="写真 (任意)" id="post-photo">
                <button
                  type="button"
                  onClick={() => pickPhoto('camera')}
                  disabled={photoProcessing}
                  className="bg-white border border-slate-300 text-slate-800 py-4 rounded-lg min-h-touch font-medium hover:bg-slate-50 disabled:opacity-50"
                >
                  📷 撮影
                </button>
                <button
                  type="button"
                  onClick={() => pickPhoto('gallery')}
                  disabled={photoProcessing}
                  className="bg-white border border-slate-300 text-slate-800 py-4 rounded-lg min-h-touch font-medium hover:bg-slate-50 disabled:opacity-50"
                >
                  🖼 ギャラリー
                </button>
              </div>
            )}
            {/* old2 PostTab L426-427 の hidden input ref パターン (camera capture / gallery で分離)。
                photoAcknowledged=false のときは render しない (テスト互換: queryByLabelText null)。 */}
            {photoAcknowledged && (
              <>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={onPhotoInputChange}
                  className="hidden"
                  aria-hidden="true"
                />
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onPhotoInputChange}
                  className="hidden"
                  aria-hidden="true"
                />
              </>
            )}
          </Card>
        );

      case 'title':
        // old2 PostTab L431-442: placeholder「短く分かりやすく」(shared-i18n post.title_placeholder)。
        return (
          <Card key={f.key} step={step} title={f.label} required={required} hint={hint}>
            <input
              id="post-title"
              aria-label={f.label}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={titleMax}
              placeholder={t('post.title_placeholder')}
              className={INPUT_CLASS}
              required={titleRequired}
            />
            <small className={COUNTER_CLASS}>
              {titleTrimmed.length} / {titleMax}
            </small>
          </Card>
        );

      case 'location':
        // old2 PostTab L444-476: 許可拒否 / 取得中バナー + 現在地で取得 button + 埋め込み地図 + 座標/精度。
        return (
          <Card
            key={f.key}
            step={step}
            title={f.label}
            required={required}
            hint={hint || '地図をタップして調整'}
          >
            {geo.state.status === 'error' && !hasLocation && (
              <div className="bg-warning-50 border border-warning-300 rounded-lg p-3 mb-2">
                <p className="text-warning-800 text-sm font-medium">
                  📍 位置情報が許可されていません
                </p>
                <p className="text-warning-700 text-xs mt-1">
                  ブラウザの設定から位置情報の利用を許可してください。または地図をタップして手動で位置を指定できます。
                </p>
              </div>
            )}
            {geo.state.status === 'pending' && !hasLocation && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-2">
                <p className="text-primary-800 text-sm">📍 現在地を取得しています…</p>
              </div>
            )}
            {/* 「現在地で取得」トグル (2026-06-12):
                  - 追跡中 + 現在地あり → 「この位置で確定」(モーション停止、locked=GPS)
                  - 追跡中 + 現在地なし → 「現在地で取得」(watch がまだ来ていないとき単発取得を試みる)
                  - 確定中 → 「現在地を追跡」(モーション復活、locked=null で追跡再開) */}
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => {
                  if (!tracking) {
                    setLocked(null); // 確定中 → 追跡再開
                  } else if (liveSample) {
                    setLocked({
                      location: liveSample.location,
                      accuracy: liveSample.accuracy,
                      source: 'gps',
                    }); // 追跡中 → この位置で確定
                  } else {
                    geo.capture(); // まだ現在地が無い → 取得を試みる
                  }
                  void deviceOrientation.requestPermissionAndStart();
                }}
                disabled={tracking && geo.state.status === 'pending'}
                className="flex-1 bg-white border border-slate-300 text-slate-800 py-3 rounded-lg min-h-touch font-medium hover:bg-slate-50 disabled:opacity-50 disabled:cursor-wait"
              >
                {!tracking
                  ? '📍 現在地を追跡'
                  : liveSample
                    ? '📍 この位置で確定'
                    : '📍 現在地で取得'}
              </button>
            </div>
            {/* geo-3 (docs/111 Wave 1): 地図は常に描画する。GPS 取得不可でも地図をタップして手動指定でき、
                エラーバナーの「地図をタップして指定できます」案内と UI の不整合 (位置ゼロから抜けられない) を解消。
                marker は位置確定後 (showMarker=hasLocation) のみ表示する。 */}
            {/* F-1 (docs/111 Wave 4): 地図 (lazy chunk) の読込失敗を picker 内で受け止め、page 全体の
                ErrorBoundary まで巻き上げない (= AppHome unmount による入力全消失を防ぐ)。失敗時も「現在地で
                取得」と入力欄は保持され、再試行 (chunk 再取得) できる。 */}
            <PageErrorBoundary
              fallback={(_err, reset) => (
                <div className="w-full h-56 rounded-lg border border-warning-300 bg-warning-50 flex flex-col items-center justify-center gap-2 p-3 text-center">
                  <p className="text-sm text-warning-800">地図を読み込めませんでした。</p>
                  <p className="text-xs text-warning-700">
                    「現在地で取得」で位置を指定できます。通信状況を確認のうえ再試行してください。
                  </p>
                  <button
                    type="button"
                    onClick={reset}
                    className="mt-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
                  >
                    地図を再読み込み
                  </button>
                </div>
              )}
            >
              <PostLocationPicker
                location={mapDisplayCenter}
                markerColor={markerColor}
                showMarker={hasLocation}
                zoom={mapZoom}
                // 追跡中=赤+モーション / 確定=赤・静止 (markerColor は pinMode 指定時は無視され常に赤)。
                pinMode={tracking ? 'tracking' : 'fixed'}
                onLocationChange={(lat, lng) =>
                  setLocked({
                    location: { latitude: lat, longitude: lng },
                    accuracy: null,
                    source: 'manual',
                  })
                }
              />
            </PageErrorBoundary>
            {effectiveLocation ? (
              <p className="text-xs text-slate-600 mt-2 flex items-center gap-2 flex-wrap">
                <span>
                  {locationSource === 'gps' ? '📍 GPS' : '🗺 手動'}{' '}
                  {effectiveLocation.latitude.toFixed(5)}, {effectiveLocation.longitude.toFixed(5)}
                </span>
                {effectiveAccuracy != null && <AccuracyBadge accuracy={effectiveAccuracy} />}
              </p>
            ) : (
              geo.state.status !== 'pending' && (
                <p className="text-xs text-slate-600 mt-2">
                  📍 現在地を取得できません。地図をタップして投稿位置を指定してください。
                </p>
              )
            )}
            {locationSource === 'gps' && effectiveAccuracy != null && effectiveAccuracy > 50 && (
              <p className="text-[11px] text-danger-600 mt-1">
                ⚠ GPS 精度が低いです。屋外や窓際で再取得すると精度が向上することがあります。
              </p>
            )}
            {areaCheck.status === 'outside' && (
              <div
                className="text-xs text-danger-700 mt-1 px-2 py-1.5 rounded-md bg-danger-50 border border-danger-200"
                role="alert"
                aria-live="polite"
              >
                <p className="font-medium mb-1">⚠ この場所では投稿できません (投稿可能エリア外)</p>
                <p>
                  中心から {(areaCheck.distanceM / 1000).toFixed(2)} km、許容半径{' '}
                  {(areaCheck.radiusM / 1000).toFixed(2)}{' '}
                  km。エリア内に移動してから再取得してください。
                </p>
                {postAreaCenter && (
                  <button
                    type="button"
                    onClick={() =>
                      setLocked({
                        location: {
                          latitude: postAreaCenter.latitude,
                          longitude: postAreaCenter.longitude,
                        },
                        accuracy: null,
                        source: 'manual',
                      })
                    }
                    className="mt-1.5 inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-white border border-danger-300 text-danger-700 hover:bg-danger-100"
                    aria-label="地区中心を投稿位置に設定する"
                  >
                    🎯 地区中心へ戻る
                  </button>
                )}
              </div>
            )}
          </Card>
        );

      case 'comment':
      case 'detail':
        // old2 PostTab L478-490: コメントは任意 (textarea)。
        return (
          <Card
            key={f.key}
            step={step}
            title={f.label}
            required={required}
            hint={required ? undefined : '任意'}
          >
            <textarea
              id="post-comment"
              aria-label={f.label}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={bodyMax}
              rows={3}
              placeholder={f.description || '気づいたこと・補足など'}
              className={`${INPUT_CLASS} resize-y`}
            />
            <small className={COUNTER_CLASS}>
              {comment.length} / {bodyMax}
            </small>
          </Card>
        );

      default:
        // old2 PostTab L492-494: その他 (category / tags / rating-scale 等) は generic renderer。
        // hint は「任意」のみ (写真/コメント Card と同じ)。補足説明 (f.description) は中の
        // PostExtraFieldsForm がラベル下の <p> で 1 回描画するため、Card hint には載せない
        // (両方に出すと補足説明が 2 か所に重複表示される不具合になる)。
        return (
          <Card
            key={f.key}
            step={step}
            title={f.label}
            required={required}
            hint={required ? undefined : '任意'}
          >
            <PostExtraFieldsForm
              fields={[f]}
              values={extraValues}
              onChange={setExtraValues}
              disabled={state.status === 'submitting'}
              hideLabel
            />
          </Card>
        );
    }
  }

  // 後方互換: postFields 未渡しの地区 (旧 v1 dataSettings 未設定) では title/comment/photo/location を
  // 固定描画する最小 fallback。本来は PostPanel が resolvePostFields で必ず渡す。
  const fallbackFields: FieldDef[] = useMemo(() => {
    if (postFields) return [];
    const mk = (
      key: string,
      label: string,
      type: FieldDef['type'],
      mode: FieldDef['mode'],
      order: number,
    ): FieldDef => ({
      key,
      label,
      type,
      mode,
      options: [],
      builtIn: true,
      description: '',
      definition: '',
      displayOrder: order,
    });
    return [
      mk('photo', '写真', 'photo', 'optional', 1),
      mk('title', 'タイトル', 'text', 'required', 2),
      mk('location', '位置情報', 'location', 'required', 3),
      mk('comment', 'コメント', 'textarea', 'optional', 4),
    ];
  }, [postFields]);

  const renderFields = postFields ? visibleFields : fallbackFields;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* old2 PostTab L497-505: 描画される Card ごとに連番 (1 始まり)。hidden で除外済の field は
          renderFields に含まれないため番号を消費しない。 */}
      {renderFields.map((f, i) => renderField(f, i + 1))}

      {missingExtraLabels.length > 0 && titleTrimmed.length > 0 && (
        <p className="text-xs text-danger-600" role="alert" aria-live="polite">
          未入力の必須項目: {missingExtraLabels.join(' / ')}
        </p>
      )}

      {/* old2 PostTab L510-514: ユーザー設定未完了の警告バナー。 */}
      {userSettingsIncomplete && (
        <div className="p-2 bg-warning-50 border border-warning-300 rounded text-warning-800 text-sm">
          ⚠ 左上のメニューからユーザ設定をしたうえで投稿してください。
        </div>
      )}

      {/* old2 PostTab L515-529: キャンセル + 投稿する の 2 button。 */}
      <div className="flex gap-3 mt-2">
        <button
          type="button"
          onClick={() => (onCancel ? onCancel() : navigate(-1))}
          className="flex-1 py-3 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          キャンセル
        </button>
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={!canSubmit}
          className="flex-1 py-3 text-base"
        >
          {state.status === 'submitting' ? t('post.submit_pending') : t('post.submit_button')}
        </Button>
      </div>

      {/* fvg-2: ボタン disable の理由を押下地点でも明示 (再選択 UI は本フォーム上部、PostPanel が表示)。 */}
      {sessionRequiredButMissing && (
        <p className="text-xs text-warning-700 m-0 text-center">
          セッションに参加すると投稿できます。上の案内からセッションを選んでください。
        </p>
      )}

      {!hasLocation && titleTrimmed.length > 0 && (
        <p className="text-xs text-slate-400 m-0 text-center">
          {locationHidden
            ? '位置情報が取得できないため投稿できません。屋外で位置情報の利用を許可してください。'
            : '地図をタップするか位置情報を取得すると投稿できます。'}
        </p>
      )}

      {state.status === 'error' && (
        <p className="text-sm text-danger-600 m-0 text-center">送信エラー: {state.message}</p>
      )}
    </form>
  );
}
